import { Link } from "react-router-dom";
import { Button } from "@odingard/design-system";
import { routeMeta, usePageMeta } from "../../lib/usePageMeta";

export interface CustomerStory {
  /** The person quoted, by name. Anonymous quotes are not proof. */
  name: string;
  role: string;
  firmType: string;
  photoUrl: string | null;
  /** One specific outcome, in their words. */
  quote: string;
}

/**
 * Named early-user stories. Empty until real customers have agreed to be
 * quoted: an unattributed or written-for-them quote is worth less than no
 * quote, because a GovCon buyer reads it as fabricated and discounts the rest
 * of the page with it.
 */
export const CUSTOMER_STORIES: CustomerStory[] = [];

/** Firms actively using Odingard Studio. Null until the number is real. */
export const FIRMS_USING: number | null = null;

export function Customers() {
  usePageMeta(routeMeta("/customers"));

  return (
    <>
      <section className="hero">
        <div className="container">
          <span className="hero__eyebrow">Customers</span>
          <h1>Who runs their bids and contracts on Odingard Studio.</h1>
          {FIRMS_USING !== null && <p className="lede">{FIRMS_USING} firms use Odingard Studio today.</p>}
        </div>
      </section>

      <section className="section">
        <div className="container">
          {CUSTOMER_STORIES.length > 0 ? (
            <div className="feature-grid">
              {CUSTOMER_STORIES.map((s) => (
                <figure className="feature" key={s.name} style={{ margin: 0 }}>
                  {s.photoUrl && (
                    <img src={s.photoUrl} alt={`${s.name}, ${s.role} at a ${s.firmType}`} width={64} height={64} style={{ borderRadius: "50%" }} />
                  )}
                  <blockquote style={{ margin: "0 0 var(--ods-space-3)" }}>{s.quote}</blockquote>
                  <figcaption>
                    <strong>{s.name}</strong>
                    <br />
                    {s.role}, {s.firmType}
                  </figcaption>
                </figure>
              ))}
            </div>
          ) : (
            <div className="section__head">
              <h2>We are early, and we would rather say so.</h2>
              <p>
                Odingard Studio is in its first year and we have not published customer stories yet. We will not write
                quotes on anyone&apos;s behalf, so this page stays empty until named users agree to be quoted with the
                outcome they actually got.
              </p>
              <p>
                What you can check today instead: the product is priced in public, you can start a free trial without
                talking to us, and{" "}
                <Link to="/marketplace/updates">the changelog</Link> shows what shipped and when. If you are using it
                and willing to be quoted, <Link to="/contact">tell us</Link>.
              </p>
              <div className="ods-row" style={{ marginTop: "var(--ods-space-4)" }}>
                <Link to="/start">
                  <Button size="lg">Start free trial</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
